import { useState, useEffect, useRef } from 'react';
import { Download, X } from 'lucide-react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export default function InstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const deferredPrompt = useRef<BeforeInstallPromptEvent | null>(null);
  const isInstalledRef = useRef(isInstalled);
  const showPromptRef = useRef(showPrompt);

  isInstalledRef.current = isInstalled;
  showPromptRef.current = showPrompt;

  useEffect(() => {
    // Check if permanently dismissed
    if (localStorage.getItem('installPromptDismissed') === 'permanent') return;

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt.current = e as BeforeInstallPromptEvent;
      if (!isInstalledRef.current && !showPromptRef.current) {
        setShowPrompt(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handler);

    const timer = setTimeout(() => {
      if (!isInstalledRef.current && !showPromptRef.current && deferredPrompt.current) {
        setShowPrompt(true);
      }
    }, 10000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt.current) return;
    await deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
      setShowPrompt(false);
    }
    deferredPrompt.current = null;
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('installPromptDismissed', 'permanent');
  };

  if (!showPrompt || isInstalled) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-50 animate-slide-up">
      <div className="glass rounded-2xl p-4 shadow-2xl border border-ocean-500/20">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-ocean-600/20 flex items-center justify-center">
              <Download className="w-5 h-5 text-ocean-400" />
            </div>
            <div>
              <h3 className="font-bold text-navy-100 text-sm">Install Draft Survey Pro</h3>
              <p className="text-xs text-navy-400 mt-0.5">Add to home screen for offline access</p>
            </div>
          </div>
          <button onClick={handleDismiss} className="p-1.5 hover:bg-navy-700/50 rounded-lg transition-colors">
            <X className="w-4 h-4 text-navy-400" />
          </button>
        </div>
        <button
          onClick={handleInstall}
          className="w-full py-2.5 bg-ocean-600 hover:bg-ocean-500 text-white rounded-xl font-semibold text-sm transition-all active:scale-[0.98]"
        >
          Install App
        </button>
      </div>
    </div>
  );
}
